import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, MapPin, Clock, Navigation, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { getFreshToken } from '../lib/firebase';
import buildWalkUrl from '../utils/walkUrl';
import formatDuration from '../utils/formatDuration';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const CompletionsPage = () => {
  const { isAuthenticated } = useAuth();
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompletions = async () => {
      const token = await getFreshToken();
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${BACKEND}/api/users/me/completions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setCompletions(data.completions || []);
        }
      } catch (e) {
        console.error('Failed to fetch completions:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCompletions();
  }, [isAuthenticated]);

  const handleRemove = async (walkId, completionId) => {
    const token = await getFreshToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND}/api/walks/${walkId}/complete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCompletions(prev => prev.filter(c => c.id !== completionId));
      }
    } catch (e) {
      console.error('Failed to remove completion:', e);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatDistance = (meters) => {
    if (!meters) return null;
    return `${(meters / 1609.34).toFixed(1)} mi`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title="My Completed Walks" noindex />
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-6 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-1">My Completed Walks</h1>
              <p className="text-gray-300">
                {loading ? 'Loading...' : `${completions.length} walk${completions.length !== 1 ? 's' : ''} completed`}
              </p>
            </div>
            <div className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-full font-bold text-2xl">
              <CheckCircle className="h-6 w-6" />
              {completions.length}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-white rounded-xl p-6">
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-gray-200 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : completions.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="h-10 w-10 text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">No walks completed yet</h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Find a walk you love and hit "Mark as Walked" to start tracking your walking journey.
            </p>
            <Link to="/walks">
              <Button className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold px-8 py-3">
                Browse Walks
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {completions.map((c) => {
              const w = c.walk;
              const walkLink = w ? buildWalkUrl(w) : '#';

              return (
                <Card key={c.id} className="bg-gradient-to-br from-amber-50 to-orange-50 border-0 hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex gap-4">
                      {/* Thumbnail */}
                      <Link to={walkLink} className="flex-shrink-0">
                        {w?.main_image ? (
                          <img
                            src={w.main_image}
                            alt={w?.title || ''}
                            className="w-24 h-24 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-amber-100 flex items-center justify-center">
                            <CheckCircle className="w-8 h-8 text-amber-500" />
                          </div>
                        )}
                      </Link>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <Link to={walkLink} className="font-bold text-gray-900 hover:text-amber-600 transition-colors text-lg block truncate">
                              {w?.title || 'Unknown Walk'}
                            </Link>
                            <p className="text-sm text-gray-500 mt-0.5">{formatDate(c.date)}</p>
                          </div>
                          <Badge className="bg-amber-500 text-white font-bold flex-shrink-0">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Walked ✓
                          </Badge>
                        </div>

                        {/* Stats */}
                        {w && (
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            {w.distance_meters && (
                              <span className="flex items-center gap-1">
                                <Navigation className="h-3 w-3" />
                                {formatDistance(w.distance_meters)}
                              </span>
                            )}
                            {w.duration_minutes && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDuration(w.duration_minutes)}
                              </span>
                            )}
                            {w.difficulty && (
                              <span className="text-xs text-gray-400">{w.difficulty}</span>
                            )}
                          </div>
                        )}

                        {/* Note */}
                        {c.note && (
                          <p className="text-sm text-gray-600 italic mt-2">"{c.note}"</p>
                        )}

                        {/* Remove */}
                        <button
                          onClick={() => handleRemove(c.walk_id, c.id)}
                          className="text-xs text-gray-400 hover:text-red-500 transition-colors mt-2 flex items-center gap-1"
                        >
                          <Trash2 className="h-3 w-3" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default CompletionsPage;
